import { Button, Divider, lighten, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import { theme } from "src/assets/theme";
import { useMemo } from "react";
import { useGetLatestWeightQuery } from "src/api/services/weightTrackerApi";
import Header from "../Headers/Header";
import Subheader from "../Headers/Subheader";
import InformationArray from "../InformationArray";
import PaymentProcessor from "../PaymentProcessor";
import Product from "../Product";
import ProductSelection from "../ProductSelection";
import ReviewsCarousel from "../ReviewsCarousel";
import Shipping from "../Shipping";
import TermsOfConditions from "../TermsOfConditions";
// import ApplyCoupon from "../Onboarding/Payment/components/ApplyCoupon";

// import { Coupon } from "src/api/types";
// import CouponView from "../Onboarding/Payment/components/CouponView";
// import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";

const InsuranceWeightLossPaymentInformation = () => {
  const isMobile = useIsMobile();
  const [selectedProduct] = useSelectedProduct();
  const { data: patientWeight } = useGetLatestWeightQuery();
  const productSlug = useProduct((p) => p.slug);
  // const { data: coupons = [] } = useGetPatientCouponsQuery({
  //   product_slug: productSlug,
  // });
  // const billingModel = useProduct((p) => p.billing_model);
  // const [coupon, setCoupon] = useState<Coupon | null>(null);
  const { t } = useTranslation();
  const page = "payment-information";

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const price = Math.max(
      selectedProduct.amount,
      selectedProduct.amount_limit ?? 0,
    );
    return price.toLocaleString();
  }, [selectedProduct]);

  const saving = useMemo(() => {
    if (selectedProduct == null) return;
    const defaultPrice = Number(defaultProductPrice.replace(",", ""));

    const comparedPrice = selectedProduct.competitor_price || 0;

    return comparedPrice - defaultPrice;
  }, [defaultProductPrice, selectedProduct]);

  const goalWeight = useMemo(() => {
    if (patientWeight?.goal == null) return null;

    return patientWeight.goal;
  }, [patientWeight?.goal]);

  const buttonText = useMemo(() => {
    return t(`${page}.${productSlug}.submit`);
  }, [productSlug, t]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product product={selectedProduct} />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          <InformationArray estimatedLoss={goalWeight} />
        </Stack>
      </Stack>
      <Stack gap={3}>
        <Shipping />
        {selectedProduct ? (
          <ProductSelection
            key={selectedProduct.stripe_id}
            item={selectedProduct}
            selectedItem={selectedProduct}
            page={page}
            prescribedPrice={defaultProductPrice}
          />
        ) : null}
        {/* <ApplyCoupon setCoupon={setCoupon} coupon={null} />
        {coupons.map((c) => (
          <CouponView coupon={c} />
        ))} */}
        <Stack gap={4}>
          <Stack gap={2}>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
            </Stack>
            {saving != null && saving > 0 ? (
              <Button
                variant="text"
                sx={{
                  backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 700,
                  borderRadius: "10px",
                  textAlign: "center",
                }}
                onClick={() => handleFocusIntoView("payment-input")}
              >
                {t(`${page}.totalSavings`, {
                  saving,
                })}
              </Button>
            ) : null}
            <ReviewsCarousel onFocus={handleFocusIntoView} />
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={null}
              dueToday={Number(defaultProductPrice)}
              button={buttonText}
            />
          ) : null}
        </Stack>
      </Stack>
      <TermsOfConditions page={page} productSlug={productSlug} isGlp1={false} />
    </Stack>
  );
};

export default InsuranceWeightLossPaymentInformation;
